import { useQuery } from '@tanstack/react-query';
import { $ } from '../lib/api';
import {
  PageHeader, StatsRow, StatCard, HomeCard, FunnelRow,
  TableWrap, Pill, Avatar, STAGE_KEYS, STAGE_SHORT,
} from '../components/ui';
import { formatMoney, formatInt } from '../lib/format';

// inc6：阶段标签统一改用 ui.tsx 的 STAGE_SHORT（含 L0/L5a/L5b 完整 10 档），删本地字典防漂移。

export function DirectorOverviewPage() {
  const { data: kpi } = useQuery({
    queryKey: ['director-kpi'],
    queryFn: () => $.get<any>('/dashboard/director'),
  });
  const { data: funnel } = useQuery({
    queryKey: ['report-funnel'],
    queryFn: () => $.get<Array<{ stage: string; cnt: number }>>('/reports/funnel'),
  });
  const { data: roi } = useQuery({
    queryKey: ['report-roi'],
    queryFn: () => $.get<Array<{ source: string; total: number; deals: number }>>('/reports/channel-roi'),
  });
  const { data: ranking } = useQuery({
    queryKey: ['report-ranking'],
    queryFn: () => $.get<Array<{ userId: number; name: string; revenue: number; deals: number; avatarUrl?: string | null }>>('/reports/sales-ranking'),
  });

  // inc6：漏斗显完整 10 档（L0 待加微信 + L1-L4 + L5a/L5b 拆开 + L6-L8），count=0 也显，便于看流失断点。
  const funnelData = funnel ?? [];
  const funnelMax = Math.max(1, ...funnelData.map((f) => Number(f.cnt)));
  const monthRev = kpi?.monthRevenue ?? 0;

  return (
    <>
      <PageHeader
        breadcrumb={['资源', '数据报表']}
        title="数据报表"
        subtitle="全公司销售运营指标总览"
      />
      <div className="work-content">
        <StatsRow>
          <StatCard label="客户总数" value={Number(kpi?.totalCustomers ?? 0)} unit="人" />
          <StatCard label="本月成交" value={Number(kpi?.monthDeals ?? 0)} unit="单" valueColor="success" />
          <StatCard label="本月营收" value={Number((monthRev / 10000).toFixed(1))} prefix="¥" suffix="万" />
          <StatCard label="客单价" value={Math.round(Number(kpi?.avgDealValue ?? 0))} prefix="¥" />
        </StatsRow>

        <div className="home-grid">
          <HomeCard
            title={<>销售漏斗（全公司）<span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>L0 → L8 全阶段</span></>}
          >
            <div className="funnel">
              {STAGE_KEYS.map((stage) => {
                const cnt = funnelData
                  .filter((f) => f.stage === stage)
                  .reduce((s, f) => s + Number(f.cnt), 0);
                const rate = funnelMax > 0 ? Math.round((cnt / funnelMax) * 100) : 0;
                return (
                  <FunnelRow
                    key={stage}
                    stage={stage}
                    label={STAGE_SHORT[stage]}
                    count={cnt}
                    rate={rate}
                    max={funnelMax}
                  />
                );
              })}
            </div>
          </HomeCard>

          <HomeCard title={<>渠道效果（本月）<span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>按客户总数排序</span></>}>
            <div style={{ padding: 18 }}>
              {(roi ?? []).length === 0
                ? <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: 16 }}>暂无数据</div>
                : (() => {
                  const sorted = [...(roi ?? [])].sort((a, b) => Number(b.total) - Number(a.total));
                  const maxTotal = Math.max(1, ...sorted.map((r) => Number(r.total)));
                  return (
                    <div>
                      {sorted.map((r) => {
                        const total = Number(r.total);
                        const deals = Number(r.deals);
                        const rate = total > 0 ? (deals / total) * 100 : 0;
                        const widthPct = (total / maxTotal) * 100;
                        return (
                          <div
                            key={r.source}
                            className="hbar-row"
                            data-tip={`总数 ${formatInt(total)} · 成交 ${formatInt(deals)} · 转化 ${rate.toFixed(1)}%`}
                          >
                            <div className="hbar-label">{r.source}</div>
                            <div className="hbar-track">
                              <div
                                className={`hbar-fill ${rate >= 50 ? 'warm' : ''}`}
                                style={{ width: `${Math.max(2, widthPct)}%` }}
                              />
                            </div>
                            <div className="hbar-val">
                              <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{formatInt(total)}</span>
                              {' '}
                              <span style={{ color: 'var(--text-3)' }}>· {rate.toFixed(0)}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
            </div>
          </HomeCard>
        </div>

        <HomeCard title={<>销售员排行 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>本月营收</span></>}>
          <TableWrap>
            <table className="ck-table">
              <thead>
                <tr>
                  <th style={{ width: '8%' }}>排名</th>
                  <th style={{ width: '32%' }}>销售员</th>
                  <th style={{ width: '22%' }}>本月营收</th>
                  <th style={{ width: '14%' }}>成交单数</th>
                  <th style={{ width: '24%' }}>表现</th>
                </tr>
              </thead>
              <tbody>
                {(ranking ?? []).length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无销售员</td></tr>
                )}
                {(ranking ?? []).map((r, i) => (
                  <tr key={r.userId}>
                    <td>
                      <span style={{
                        fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                        color: i < 3 ? 'var(--warning-text)' : 'var(--text-3)',
                      }}>#{i + 1}</span>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Avatar userId={r.userId} src={r.avatarUrl ?? null} name={r.name ?? `#${r.userId}`} size="md" />
                        <span style={{ fontSize: 14 }}>{r.name ?? `#${r.userId}`}</span>
                      </span>
                    </td>
                    <td><span className="money large">{formatMoney(r.revenue)}</span></td>
                    <td><span className="money">{formatInt(r.deals)}</span></td>
                    <td>
                      {r.deals === 0
                        ? <Pill tone="default">未开单</Pill>
                        : i === 0 ? <Pill tone="success">🏆 月度冠军</Pill>
                        : i < 3 ? <Pill tone="primary">前三</Pill>
                        : <Pill tone="default">达标</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}
